/* eslint-disable */
import { useState } from "react";

/**
 * Feed image display for a Community post.
 * - 1 image  → full-width responsive
 * - 2 images → 50/50 side-by-side
 * - 3 images → hero left + 2 stacked right
 * - 4 images → 2×2 grid
 * Tap any image to open the lightbox.
 */
export default function PostImages({ images }) {
  const [lightbox, setLightbox] = useState(null); // {index}

  if (!images || images.length === 0) return null;
  const n = Math.min(images.length, 4);

  const open = (i) => setLightbox({ index: i });
  const close = () => setLightbox(null);
  const nav = (dir) => setLightbox((lb) => {
    if (!lb) return lb;
    const next = (lb.index + dir + n) % n;
    return { index: next };
  });

  return (
    <>
      <div
        data-testid="post-images"
        data-count={n}
        style={{
          display: "grid",
          gap: 4,
          marginTop: 12,
          borderRadius: 12,
          overflow: "hidden",
          ...(n === 1 && { gridTemplateColumns: "1fr" }),
          ...(n === 2 && { gridTemplateColumns: "1fr 1fr", aspectRatio: "16/9" }),
          ...(n === 3 && { gridTemplateColumns: "2fr 1fr", gridTemplateRows: "1fr 1fr", aspectRatio: "16/10" }),
          ...(n === 4 && { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", aspectRatio: "1/1" }),
        }}
      >
        {images.slice(0, 4).map((img, idx) => {
          const style = n === 3 && idx === 0 ? { gridRow: "1 / span 2" } : {};
          return (
            <button
              key={img.image_id}
              type="button"
              onClick={() => open(idx)}
              aria-label={`Open image ${idx + 1} of ${n}`}
              data-testid={`post-image-${idx}`}
              style={{
                ...style,
                border: "none",
                padding: 0,
                cursor: "zoom-in",
                background: "rgba(27,35,64,0.05)",
                position: "relative",
                aspectRatio: n === 1 ? `${img.width}/${img.height}` : undefined,
                overflow: "hidden",
              }}
            >
              <img
                src={img.image_url}
                alt={img.alt_text || `Post image ${idx + 1}`}
                loading="lazy"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
              {idx === 3 && images.length > 4 && (
                <div style={{
                  position: "absolute", inset: 0,
                  background: "rgba(27,35,64,0.55)",
                  color: "#faf8f3",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 24, fontWeight: 700,
                }}>+{images.length - 4}</div>
              )}
            </button>
          );
        })}
      </div>
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="post-image-lightbox"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
          onKeyDown={(e) => { if (e.key === "Escape") close(); if (e.key === "ArrowLeft") nav(-1); if (e.key === "ArrowRight") nav(1); }}
          tabIndex={-1}
          style={{
            position: "fixed", inset: 0, background: "rgba(27,35,64,0.94)",
            zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24,
          }}
        >
          <button type="button" onClick={close} aria-label="Close"
            style={{ position: "absolute", top: 20, right: 20, background: "transparent", border: "none", color: "#faf8f3", fontSize: 34, cursor: "pointer" }}>×</button>
          {n > 1 && (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); nav(-1); }}
                aria-label="Previous image"
                style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.4)", border: "none", color: "#faf8f3", fontSize: 30, cursor: "pointer", width: 48, height: 48, borderRadius: "50%" }}>‹</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); nav(1); }}
                aria-label="Next image"
                style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.4)", border: "none", color: "#faf8f3", fontSize: 30, cursor: "pointer", width: 48, height: 48, borderRadius: "50%" }}>›</button>
            </>
          )}
          <img
            src={images[lightbox.index].image_url}
            alt={images[lightbox.index].alt_text || ""}
            style={{ maxWidth: "100%", maxHeight: "88vh", objectFit: "contain", borderRadius: 6 }}
          />
          <div style={{ position: "absolute", bottom: 20, color: "#faf8f3", fontSize: 12, opacity: 0.7, letterSpacing: 0.16 }}>
            {lightbox.index + 1} / {n}
          </div>
        </div>
      )}
    </>
  );
}
